import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, createVNode as _createVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-info text-bold" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_3 = { class: "text-navy-700 text-bold" }
const _hoisted_4 = { class: "text-info text-bold" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_6 = { class: "text-navy-700 text-bold" }

import SfxTable from "src/components/table/SfxTable.vue"
import { getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { useSearchStore } from "src/stores/searchStore"
import { useSupportStore } from "src/stores/supportStore"
import { Log } from "src/util/classes/log"
import { MissingEntityType } from "src/util/enums/entityType"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import {
  fetchCorrectiveWorkOnItemListData,
  fetchIssueOnItemListData,
  fetchIssuePriorityListData,
  fetchItemClassListData,
  fetchItemListData,
  fetchItemModelListData,
} from "src/util/functions/entityData"
import { getIssuePriorityColor } from "src/util/functions/issuePriority"
import { sortIssueData, toIssueTableData } from "src/util/functions/issues"
import { getTemporalInfo } from "src/util/functions/time"
import { getWorkspaceHomeBreadcrumb } from "src/util/functions/workspace"
import { Breadcrumbs } from "src/util/types/components/sfxBreadcrumb"
import { RowData, StatusColumnNames, TableColumn } from "src/util/types/components/sfxTable"
import { IssuesOnItem, IssueTableData } from "src/util/types/dataModels/issue"
import { WorkOrderCorrectiveItem } from "src/util/types/dataModels/workOrder"
import { computed, onBeforeMount, PropType, ref, watch, watchEffect } from "vue"
import { useI18n } from "vue-i18n"
import { useFilterStore } from "src/stores/filterStore"
import {
  filterIssuesBasedOnItems,
  filterIssuesBasedOnRegularWorkRaisedFrom,
} from "src/util/functions/filters"

//region Props and Emits

const statusColumnCount = 4


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxWorkOrderIssueItemAssignment',
  props: {
  workOrder: {
    type: Object as PropType<WorkOrderCorrectiveItem>,
    required: true,
  },

  entitiesIncluded: {
    type: Array as PropType<IssuesOnItem[]>,
    required: true,
  },

  entitiesPerformed: {
    type: Array as PropType<IssuesOnItem[]>,
    required: true,
  },

  isDisplayingToDoEntities: {
    type: Boolean,
  },

  isReadOnly: {
    type: Boolean,
  },
},
  setup(__props, { expose: __expose }) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const filterStore = useFilterStore()
const supportStore = useSupportStore()
const searchStore = useSearchStore()
const cacheStore = useCacheStore()

const breadcrumbs = ref<Breadcrumbs>([])

const sfxTableAvailable = ref<typeof SfxTable | undefined>()
const sfxTableCompleted = ref<typeof SfxTable | undefined>()
const filteredAvailableDataCount = computed(() => sfxTableAvailable.value?.filteredDataCount || 0)
const filteredCompletedDataCount = computed(() => sfxTableCompleted.value?.filteredDataCount || 0)

const issueAvailableList = ref<RowData<IssueTableData>[]>([])
const issueAvailableListFiltered = ref<RowData<IssueTableData>[]>([])
const issueTableAvailableData = computed(() => issueAvailableListFiltered.value)
const issueCompletedList = ref<RowData<IssueTableData>[]>([])
const issueTableCompletedData = computed(() => issueCompletedList.value)
const noData = ref(MissingEntityType.ISSUE_NOT_FOUND)
const noMatchingData = ref(MissingEntityType.ISSUE_NO_MATCHES)
const loadingData = ref(true)

const mainColumn = (row: RowData<IssueTableData>) => row.issueData.title
const uniqueColumn = (row: RowData<IssueTableData>) => row.issueData.id

const mainColumnLabel = ref(t("issues.table_column_title_label"))

const selectionEnabled = ref(!props.isReadOnly)
const selected = ref<IssueTableData[]>([])
const initialSelected = ref<IssueTableData[]>([])

const initialSelectIds = computed(() => initialSelected.value.map((i) => i.issueData.id).sort())
const selectIds = computed(() => selected.value.map((i) => i.issueData.id).sort())

const visibleColumns = ref([
  "work_domain",
  "entity_grouping",
  "entity",
  "layout",
  "raised",
  "priority",
])

const issueColumns = ref<Array<TableColumn>>([
  {
    name: "work_domain",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_work_domain_label"),
    field: (row: IssueTableData) => row.workDomainData?.designation,
    id: (row: IssueTableData) => row.workDomainData?.id,
  },
  {
    name: "entity_grouping",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_entity_grouping_label"),
    field: (row: IssueTableData) => row.itemClassData?.designation,
    id: (row: IssueTableData) => row.itemClassData?.id,
  },
  {
    name: "entity",
    switchable: true,
    sortable: true,
    searchable: true,
    label: t("issues.table_column_entity_label"),
    field: (row: IssueTableData) => row.actionableItem?.designation, // TODO: Add location ID at the beginning
    id: (row: IssueTableData) => row.actionableItem?.id,
  },
  {
    name: "layout",
    switchable: true,
    sortable: true,
    emptyText: t("sfxTable.empty_no_location"),
    label: t("items.table_column_layout_label"),
    field: "", // TODO: Should display layout designation when we have it
  },
  {
    name: "raised",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_raised_label"),
    field: (row: IssueTableData) =>
      new Date(row.issueData.declared_dt || row.issueData.created_dt).toJSON(),
    format: (value: string | null) => (value ? getTemporalInfo(value, "card") : null),
  },
  {
    name: "priority",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_priority_label"),
    align: "left",
    field: (row: IssueTableData) => row.priorityData?.risk_factor + "-" + row.priorityData?.label,
    id: (row: IssueTableData) => row.priorityData?.id,
  },
  {
    name: "closed_date",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_closed_date_label"),
    field: (row: IssueTableData) => new Date(row.issueData.concluded_dt || "").toJSON(),
    format: (value: string | null) => (value ? getTemporalInfo(value, "card") : null),
  },
  {
    name: "last_updated",
    switchable: true,
    sortable: true,
    label: t("issues.table_column_updated_label"),
    field: (row: IssueTableData) => new Date(row.issueData.updated_dt || "").toJSON(),
    format: (value: string | null) => (value ? getTemporalInfo(value, "card") : null),
  },
])

const statusColumnsSort: StatusColumnNames[] = [
  "issue-overdue",
  "work-order",
  "corrective-work",
  "corrective-work-accepted",
  "closed",
]

// 1. issue-overdue
// 2. work-order
// 3. corrective-work
// 4. corrective-work-accepted
// We don't display the closed column
const performedRowClass = ref("sfx-table__td-status-performed")

//endregion

//region Lifecycle

onBeforeMount(async () => {
  searchStore.domain = "issues"
  supportStore.setFeedbackData(t("workOrder.title_work_order_issues"))

  try {
    await fetchAPIData()
    await assignIssueTableData()
  } catch (fetchError) {
    Log.error({
      message: "There was an error trying to fetch and populate data for Issues",
      error: fetchError,
    })
  } finally {
    loadingData.value = false
  }
})

//endregion

//region Functions

function resetSelection() {
  selected.value = [...initialSelected.value]
}

async function fetchAPIData() {
  await fetchItemClassListData()
  await Promise.all([
    fetchItemListData(),
    fetchIssueOnItemListData(),
    fetchIssuePriorityListData(),
    fetchItemModelListData(),
    fetchCorrectiveWorkOnItemListData(),
  ])
}

function issueClasses(row: IssueTableData): string {
  if (row.overdueTime && row.overdueTime > 0) return "sfx-table__td-status-overdue"
  if (row.issueData?.concluded_dt) return "sfx-table__td-status-closed"
  return ""
}

async function assignIssueTableData() {
  const issueWithOpenCorrectiveWorkIds = cacheStore.issueWithOpenCorrectiveWork.keys()

  const skipIds = props.entitiesPerformed
    .map((i) => i.id)
    .concat(props.entitiesIncluded.map((i) => i.id))
    .concat([...issueWithOpenCorrectiveWorkIds])

  const issueData = await getIssueData(props.workOrder?.work_domain, skipIds)

  const selectedIssues = await Promise.all(props.entitiesIncluded.map((i) => toIssueTableData(i)))
  selected.value = selectedIssues
  initialSelected.value = [...selectedIssues]

  issueAvailableList.value = issueData.concat(selectedIssues).sort(sortIssueData)
  issueAvailableListFiltered.value = issueAvailableList.value

  issueCompletedList.value = await Promise.all(
    props.entitiesPerformed.map(async (i) => {
      const data = await toIssueTableData(i)
      data.preSelectState = true
      return data
    })
  )
}

async function getIssueData(
  workDomainId: string,
  skipIssueIds: string[]
): Promise<RowData<IssueTableData>[]> {
  const db = await getDB()

  // Only issue on items
  // Only for the work domain of the selected work order
  // Not in any work order
  // Not closed
  // Without a corrective work

  const issueDocuments = await db.issues_on_item
    .find({
      selector: {
        $and: [
          { work_domain: workDomainId },
          { corrective_work_order: null },
          { concluded_dt: null },
          { id: { $nin: skipIssueIds } },
        ],
      },
    })
    .exec()

  return await Promise.all(
    issueDocuments.map<Promise<RowData<IssueTableData>>>((i) => toIssueTableData(i))
  )
}

//endregion

//region Setup Code

watchEffect(async () => {
  const count = props.isDisplayingToDoEntities
    ? filteredAvailableDataCount.value
    : filteredCompletedDataCount.value
  const total = props.isDisplayingToDoEntities
    ? issueTableAvailableData.value.length
    : issueTableCompletedData.value.length

  breadcrumbs.value = [
    await getWorkspaceHomeBreadcrumb(),
    {
      text: t("workOrder.title"),
      link: { name: NamedManagerPath.SELECT_WORK_ORDER },
      highlight: true,
    },
    {
      text: t("workOrder.title_simple"),
    },
    {
      text: t("workOrder.title_work_order_issues"),
    },
    { current: count, total, entity: "issue" },
  ]
})

watch(
  () => [filterStore.issuesPageFilters, issueAvailableList.value],
  () => {
    if (!filterStore.issuesPageFilters.hasFilters) {
      issueAvailableListFiltered.value = issueAvailableList.value
    } else {
      issueAvailableListFiltered.value = issueAvailableList.value.filter(
        (issue) =>
          filterIssuesBasedOnItems(issue) && filterIssuesBasedOnRegularWorkRaisedFrom(issue)
      )
    }
  },
  { deep: true }
)

__expose({
  resetSelection,
  breadcrumbs,
  initialSelectIds,
  selectIds,
})

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(SfxTable, {
        ref_key: "sfxTableAvailable",
        ref: sfxTableAvailable,
        columns: issueColumns.value,
        "visible-columns": visibleColumns.value,
        loading: loadingData.value,
        data: issueTableAvailableData.value,
        "no-data": noData.value,
        "no-matching-data": noMatchingData.value,
        "unique-column": uniqueColumn,
        "main-column": mainColumn,
        "main-column-label": mainColumnLabel.value,
        "selection-enabled": selectionEnabled.value,
        "status-columns-sort": statusColumnsSort,
        "status-columns-count": statusColumnCount,
        selected: selected.value,
        "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((selected).value = $event)),
        "has-filters": _unref(filterStore).issuesPageFilters.hasFilters,
        "entity-for-work-order-page": "",
        entity: "issue",
        "row-class": issueClasses,
        "enable-select-only-mode": ""
      }, {
        "body-cell-entity_grouping-prepend": _withCtx(({ row }) => [
          _createElementVNode("span", _hoisted_1, [
            _createTextVNode(_toDisplayString(row?.actionableItem
              ? _ctx.$t("issues.table_column_entity_grouping_item_class")
              : _ctx.$t("issues.table_column_entity_grouping_area_attribute_set")) + " ", 1),
            _hoisted_2
          ])
        ]),
        "body-cell-priority-prepend": _withCtx(({ row }) => [
          _createVNode(_component_q_icon, {
            name: _unref(Icons).ISSUE,
            style: _normalizeStyle({ color: row?.priorityData?.color }),
            class: "q-pr-sm",
            size: "sm"
          }, null, 8, ["name", "style"])
        ]),
        "body-cell-priority-value": _withCtx(({ value }) => [
          _createElementVNode("span", _hoisted_3, _toDisplayString(value), 1)
        ]),
        _: 1
      }, 8, ["columns", "visible-columns", "loading", "data", "no-data", "no-matching-data", "main-column-label", "selection-enabled", "selected", "has-filters"])
    ], 512), [
      [_vShow, props.isDisplayingToDoEntities]
    ]),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(SfxTable, {
        ref_key: "sfxTableCompleted",
        ref: sfxTableCompleted,
        columns: issueColumns.value,
        "visible-columns": visibleColumns.value,
        loading: loadingData.value,
        data: issueTableCompletedData.value,
        "no-data": noData.value,
        "no-matching-data": noMatchingData.value,
        "unique-column": uniqueColumn,
        "main-column": mainColumn,
        "main-column-label": mainColumnLabel.value,
        "status-columns-sort": statusColumnsSort,
        "status-columns-count": statusColumnCount,
        entity: "issue",
        "selection-enabled": selectionEnabled.value,
        enablePreSelectedMode: "",
        "row-class": performedRowClass.value,
        "icon-filter": false
      }, {
        "body-cell-entity_grouping-prepend": _withCtx(({ row }) => [
          _createElementVNode("span", _hoisted_4, [
            _createTextVNode(_toDisplayString(row?.actionableItem
              ? _ctx.$t("issues.table_column_entity_grouping_item_class")
              : _ctx.$t("issues.table_column_entity_grouping_area_attribute_set")) + " ", 1),
            _hoisted_5
          ])
        ]),
        "body-cell-priority-prepend": _withCtx(({ row }) => [
          _createVNode(_component_q_icon, {
            name: _unref(Icons).ISSUE,
            color: _unref(getIssuePriorityColor)(row?.priorityData),
            class: "q-pr-sm",
            size: "sm"
          }, null, 8, ["name", "color"])
        ]),
        "body-cell-priority-value": _withCtx(({ value }) => [
          _createElementVNode("span", _hoisted_6, _toDisplayString(value), 1)
        ]),
        _: 1
      }, 8, ["columns", "visible-columns", "loading", "data", "no-data", "no-matching-data", "main-column-label", "selection-enabled", "row-class"])
    ], 512), [
      [_vShow, !props.isDisplayingToDoEntities]
    ])
  ], 64))
}
}

})